import { Server, Users, Layers } from "lucide-react";
import { StatusLED } from "@/components/infra";

/* ============================================================
 * PRIMER — Three plain-language answers
 *   What is digital infrastructure?
 *   What is a data center?
 *   How we picked the name and logo.
 * Sits between Mission (01) and Why Now (02).
 * ============================================================ */

const CARDS = [
  {
    code: "P.01",
    eyebrow: "/ Digital Infrastructure",
    title: "The physical layer behind every byte.",
    body: "Data centers, fiber, subsea cables, edge sites, power, and cooling. Everything that makes the digital world physical. When you stream a video or query a model, you are touching this layer.",
    icon: Layers,
  },
  {
    code: "P.02",
    eyebrow: "/ Data Center",
    title: "Factories for compute.",
    body: "A purpose-built facility packed with servers, switches, storage, power gear, and cooling. They house the compute behind every app, every model, every stream. AI is now the biggest customer they have ever had.",
    icon: Server,
  },
];

export default function Primer() {
  return (
    <section
      id="primer"
      data-testid="primer-section"
      className="border-b hairline bg-white relative overflow-hidden"
    >
      {/* Soft chrome wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #0F1117 0 1px, transparent 1px 4px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Eyebrow + headline */}
        <div className="grid grid-cols-12 gap-y-8 gap-x-6 mb-12 md:mb-16">
          <div className="col-span-12 lg:col-span-5">
            <div data-testid="primer-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-5 flex items-center gap-3">
              <StatusLED />
              / Back to basics
            </div>
            <h2
              data-testid="primer-headline"
              className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance"
            >
              Three questions,<br />
              <span className="text-chrome">plain answers.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-xl">
              No jargon. No marketing fluff. If you are new to the industry, start
              here. Three things worth knowing before any conversation about
              digital infrastructure goes further.
            </p>
          </div>
        </div>

        {/* Three cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {CARDS.map((c) => (
            <PrimerCard key={c.code} card={c} />
          ))}

          {/* Third card — the name and logo origin (custom layout) */}
          <article
            data-testid="primer-card-logo"
            className="chrome-surface border hairline p-6 md:p-7 flex flex-col md:col-span-2 lg:col-span-1"
          >
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono-label text-[10.5px] tracking-[0.16em] text-[#5F6168]">
                / The Name &amp; Logo
              </span>
              <span className="font-mono-label text-[9.5px] tracking-[0.14em] text-[#71747B]">
                P.03
              </span>
            </div>

            <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-[#0F1117] mb-3 leading-[1.15]">
              Servers + Community = DCYB.
            </h3>

            <p className="text-[14.5px] text-[#3D4049] leading-relaxed mb-5">
              Data Center Youngbloods. The next generation entering the industry.
              The chrome mark fuses two ideas into one silhouette. A server rack
              up top. A group of people below. The infrastructure cannot run
              without the people who run it.
            </p>

            {/* Inline concept equation: Server + People = DCYB */}
            <div
              data-testid="primer-logo-concept"
              className="mt-auto bg-[#F4F5F7] border hairline p-3 sm:p-4 md:p-5 flex items-center justify-between gap-1 sm:gap-3"
            >
              <ConceptCell label="Servers">
                <Server size={26} className="text-[#0F1117]" strokeWidth={1.6} />
              </ConceptCell>
              <Operator>+</Operator>
              <ConceptCell label="Community">
                <Users size={26} className="text-[#0F1117]" strokeWidth={1.6} />
              </ConceptCell>
              <Operator>=</Operator>
              <ConceptCell label="DCYB">
                <img
                  src="/dcyb-logo.png"
                  alt=""
                  aria-hidden
                  className="w-8 h-8 sm:w-9 sm:h-9 object-contain"
                  draggable="false"
                />
              </ConceptCell>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function PrimerCard({ card }) {
  const Icon = card.icon;
  return (
    <article
      data-testid={`primer-card-${card.code.toLowerCase()}`}
      className="chrome-surface border hairline p-6 md:p-7 flex flex-col"
    >
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono-label text-[10.5px] tracking-[0.16em] text-[#5F6168]">
          {card.eyebrow}
        </span>
        <span className="font-mono-label text-[9.5px] tracking-[0.14em] text-[#71747B]">
          {card.code}
        </span>
      </div>

      <div className="mb-5 inline-flex items-center justify-center w-12 h-12 border hairline bg-white">
        <Icon size={22} className="text-[#0F1117]" strokeWidth={1.6} />
      </div>

      <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-[#0F1117] mb-3 leading-[1.15]">
        {card.title}
      </h3>

      <p className="text-[14.5px] text-[#3D4049] leading-relaxed flex-1">
        {card.body}
      </p>
    </article>
  );
}

function ConceptCell({ label, children }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col items-center text-center gap-2">
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white border hairline flex items-center justify-center">
        {children}
      </div>
      <span className="font-mono-label text-[8px] sm:text-[9px] tracking-[0.04em] sm:tracking-[0.14em] text-[#52555B] uppercase">
        {label}
      </span>
    </div>
  );
}

function Operator({ children }) {
  return (
    <span
      aria-hidden
      className="font-display text-xl font-bold text-[#71747B] shrink-0 pb-5"
    >
      {children}
    </span>
  );
}
